import { HandshakeOutline, PointOfView, RockUphill } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import { TypographyWithBulletPoint } from '@/components/TypographyWithBulletPoint';
import { ThreeFieldDelimiter } from '@/modules/knowledge-asset/KnowledgeAssetCreate/components/KnowledgeAssetCreateForm/component/TwoFieldDynamicTextList';

import EmptyState from '../../../KnowledgeAssetDetails/components/EmptyState';

interface POVTrendPageProps {
  solutionName: string;
  description: string;
  highlights: string[];
  painPoints: string[];
  trendsAndPOV: string[];
  targetPersonas: {
    practitioners: any[];
    executiveBuyers: any[];
    decisionMakers: any[];
  };
  platformCapabilities: string[];
  productCapabilities: string[];
  typicalIntegrations: string[];
  differentiators: string[];
  successKeyMetrics: string[];
  competitors: string[];
  marketChallenges?: string[];
  businessOutcomes?: string[];
}

const POVTrendPage = ({
  solutionName,
  trendsAndPOV,
  marketChallenges = [],
  businessOutcomes = [],
}: POVTrendPageProps) => {
  const marketAndOutcomes = [
    {
      title: 'Market Challenges',
      icon: RockUphill,
      items: marketChallenges || [],
    },
    {
      title: 'Business Outcomes',
      icon: HandshakeOutline,
      items: businessOutcomes || [],
    },
  ];

  // Parse POVs with three field delimiters
  const povs = trendsAndPOV.map((pov) => {
    const [title = '', descAndKpis = ''] = pov
      .split(ThreeFieldDelimiter.TITLE_DESC)
      .map((s) => s.trim());
    const [description = '', kpisPart = ''] = descAndKpis
      .split(ThreeFieldDelimiter.DESC_KPIS)
      .map((s) => s.trim());
    const kpis = kpisPart ? kpisPart.split(ThreeFieldDelimiter.KPI).map((k) => k.trim()) : [];

    return {
      title,
      description,
      kpis,
    };
  });

  return (
    <Stack className="max-w-[101.2rem] min-w-[75rem] mx-64 mt-48 mb-128" direction="col" gapY={21}>
      <Typography variant="display-xl">Trend/POV</Typography>

      <Stack className="grid grid-cols-2 gap-16">
        {marketAndOutcomes.map((card, index) => (
          <Stack
            key={index}
            className="border border-border rounded-12 p-20"
            direction="col"
            gapY={12}>
            <Stack gapX={8}>
              <Stack className="flex-shrink-0">
                <card.icon fillColorToken="icon-active" height={24} width={24} />
              </Stack>
              <Typography variant="heading-xs">{card.title}</Typography>
            </Stack>
            <Stack direction="col" gapY={8}>
              {card.items.length > 0 ? (
                card.items.map((item: string, itemIndex: number) => (
                  <TypographyWithBulletPoint
                    key={itemIndex}
                    className="text-text-secondary"
                    text={item}
                    variant="body-long-01"
                  />
                ))
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>

      <Stack className="grid grid-cols-2 gap-16">
        {povs.map((pov, index) => (
          <Stack
            key={index}
            className="border border-border rounded-12 p-20"
            direction="col"
            gapY={12}>
            <Stack gapX={8}>
              <Stack className="flex-shrink-0">
                <PointOfView fillColorToken="icon-active" height={24} width={24} />
              </Stack>
              <Typography variant="heading-xs">{pov.title || 'N/A'}</Typography>
            </Stack>
            {pov.description ? (
              <Typography className="text-text-secondary" variant="body-long-01">
                {pov.description}
              </Typography>
            ) : (
              <EmptyState />
            )}
            {pov.kpis.length > 0 && (
              <Stack direction="col" gapY={8}>
                <Typography variant="heading-xs">KPIs & Success Metrics</Typography>
                <Stack direction="col">
                  {pov.kpis.map((kpi: string, kpiIndex: number) => (
                    <Stack
                      key={kpiIndex}
                      className={cn(
                        'py-12',
                        kpiIndex !== pov.kpis.length - 1 && 'border-b border-border',
                      )}
                      direction="col"
                      gapY={4}>
                      <Typography className="text-text-secondary" variant="body-long-01">
                        {kpi}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

export default POVTrendPage;
